<template>
  <div class="quick-image-page app-page">
    <header class="app-page-header quick-header">
      <div class="quick-header-row">
        <!-- 返回创作页按钮（对话模式） -->
        <button v-if="conversationId" type="button" class="back-link" @click="$router.push('/create').catch(()=>{})">← 创作</button>
        <!-- 对话标题（可编辑） -->
        <div class="conv-title-wrap">
          <span
            v-if="editingTitleId !== conversationId"
            class="conv-title"
            :title="conversationTitle"
            @dblclick="startEditTitle"
          >{{ conversationTitle || '图片快速创作' }}</span>
          <input
            v-else
            ref="titleEditInput"
            v-model="editingTitleValue"
            class="conv-title-input"
            type="text"
            maxlength="80"
            @blur="commitEditTitle"
            @keydown.enter="commitEditTitle"
            @keydown.escape="cancelEditTitle"
          />
          <button v-if="conversationId && editingTitleId !== conversationId" type="button" class="edit-title-btn" title="编辑标题" @click="startEditTitle">✎</button>
        </div>
      </div>
    </header>

    <div ref="feedScroll" class="quick-feed" @scroll="onFeedScroll">
      <div v-if="loadingMore" class="feed-hint">加载中…</div>
      <div v-if="!jobs.length && !listLoading" class="feed-hint">暂无记录，在下方输入描述并生成。</div>
      <article v-for="job in jobsChronological" :key="'j-' + job.taskId" class="feed-card">
        <div class="feed-card-head">
          <span>{{ formatModelLabel(job.modelCode) }} · {{ job.aspectRatio }} · {{ job.resolution }}</span>
          <span class="feed-time">{{ formatTime(job.createTime) }}</span>
        </div>
        <div class="feed-prompt">{{ job.prompt }}</div>
        <!-- 生成中 -->
        <div v-if="job.status === 'pending' || job.status === 'running'" class="feed-status ok">
          {{ job.message || '生成中…' }}<span v-if="job.progress">（{{ job.progress }}%）</span>
        </div>
        <!-- 失败 -->
        <div v-else-if="job.status === 'failed'" class="feed-status err">
          {{ job.errorMessage || '生成失败' }}
          <button type="button" class="link" @click="retryFromJob(job)">重新编辑</button>
        </div>
        <!-- 成功显示图片 -->
        <div v-else-if="job.imageUrl" class="feed-image-box">
          <img
            class="feed-image"
            :src="mediaUrl(job.imageUrl)"
            alt="生成图片"
            @click="openPreview(job.imageUrl)"
          />
          <div class="feed-actions">
            <button type="button" class="btn" @click="retryFromJob(job)">重新编辑</button>
            <button type="button" class="btn" @click="regenerateFromJob(job)">再次生成</button>
          </div>
        </div>
      </article>
    </div>

    <!-- 创作表单 -->
    <div class="composer">
      <div class="prompt-wrap">
        <div
          ref="promptEditor"
          class="prompt-editor"
          contenteditable="true"
          role="textbox"
          data-placeholder="输入图片描述"
          @input="onPromptInput"
          @keydown.enter.exact.prevent="handleSubmit"
        ></div>
      </div>
      <div class="toolbar">
        <select v-model="selectedModel" class="tb-sel">
          <option v-for="o in modelOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>
        <select v-model="selectedAspectRatio" class="tb-sel">
          <option v-for="r in aspectRatioOptions" :key="r.value" :value="r.value">{{ r.label }}</option>
        </select>
        <select v-model="selectedResolution" class="tb-sel">
          <option v-for="r in resolutionOptions" :key="r" :value="r">{{ r }}</option>
        </select>
        <button
          type="button"
          class="tb-send"
          :disabled="submitting"
          @click="handleSubmit"
        >
          {{ submitting ? '生成中…' : '发送 · ' + pointsDisplay + ' 积分' }}
        </button>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <div v-if="previewSrc" class="mask" @click.self="previewSrc = ''">
      <div class="preview-wrap">
        <button type="button" class="preview-close" @click="previewSrc = ''">×</button>
        <img class="preview-img" :src="mediaUrl(previewSrc)" alt="预览" />
      </div>
    </div>
  </div>
</template>

<script>
import { userApi } from "../api/user";

export default {
  name: "QuickImagePage",
  data() {
    return {
      conversationId: null,
      conversationTitle: "",
      editingTitleId: null,
      editingTitleValue: "",
      jobs: [],
      listLoading: false,
      loadingMore: false,
      page: 1,
      hasMore: true,
      submitting: false,
      _batchPollTimer: null,
      _batchPollInFlight: false,
      _batchPollMs: 10000,
      selectedModel: "",
      selectedAspectRatio: "1:1",
      selectedResolution: "1K",
      imageModelsRaw: [],
      previewSrc: ""
    };
  },
  computed: {
    jobsChronological() {
      return [...this.jobs].sort((a, b) => Number(a.taskId) - Number(b.taskId));
    },
    modelOptions() {
      return (this.imageModelsRaw || []).map(m => ({
        value: String(m.code || ""),
        label: String(m.name || m.code || "")
      }));
    },
    currentImageModel() {
      return (this.imageModelsRaw || []).find(m => m.code === this.selectedModel) || null;
    },
    aspectRatioOptions() {
      return [
        { label: "1:1 正方形", value: "1:1" },
        { label: "16:9 横屏", value: "16:9" },
        { label: "9:16 竖屏", value: "9:16" },
        { label: "4:3 标准", value: "4:3" }
      ];
    },
    resolutionOptions() {
      return ["1K", "2K"];
    },
    pointsPerJob() {
      // 统一使用 imageModels 返回的 pointsPerCall，不走任何前端兜底值，
      // 确保与数据库 fa_ai_points_config 一致。
      const m = this.currentImageModel;
      if (m && Number(m.pointsPerCall) > 0) return Number(m.pointsPerCall);
      return 0; // 后端未配置时显示 0，由 handleSubmit 拦截提示
    },
    pointsDisplay() {
      return String(this.pointsPerJob || 0);
    }
  },
  async mounted() {
    const idParam = this.$route && this.$route.query && this.$route.query.id;
    this.conversationId = idParam ? Number(idParam) : null;
    if (this.conversationId) {
      await this.loadConversation();
    }
    await this.loadModels();
    await this.loadJobs(true);
    this.$nextTick(() => this.scrollBottom());
    if (typeof document !== "undefined") {
      this._visHandler = () => {
        if (!document.hidden) {
          if (this.jobs && this.jobs.some(j => j.status === "pending" || j.status === "running")) {
            this.ensureBatchPoll();
            void this.tickBatchPoll();
          }
        }
      };
      document.addEventListener("visibilitychange", this._visHandler);
    }
  },
  beforeDestroy() {
    if (this._batchPollTimer) { clearTimeout(this._batchPollTimer); this._batchPollTimer = null; }
    if (typeof document !== "undefined" && this._visHandler) {
      document.removeEventListener("visibilitychange", this._visHandler);
      this._visHandler = null;
    }
  },
  methods: {
    mediaUrl(s) {
      s = String(s || "").trim();
      if (!s) return "";
      return /^https?:\/\//i.test(s) ? s : (s.startsWith("/") ? s : `/${s}`);
    },
    formatTime(ts) {
      if (!ts) return "";
      const d = new Date(Number(ts) * 1000);
      return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    },
    formatModelLabel(code) {
      const r = (this.imageModelsRaw || []).find(m => m.code === code);
      return r ? (r.name || r.code) : code || "—";
    },
    openPreview(src) {
      this.previewSrc = src;
    },

    // 对话标题
    async loadConversation() {
      if (!this.conversationId) return;
      try {
        const conv = await userApi.conversationDetail({ id: this.conversationId });
        if (conv) this.conversationTitle = String(conv.title || "").trim() || "图片快速创作";
      } catch (e) {
        this.conversationTitle = "图片快速创作";
      }
    },
    startEditTitle() {
      if (!this.conversationId) return;
      this.editingTitleId = this.conversationId;
      this.editingTitleValue = this.conversationTitle;
      this.$nextTick(() => {
        const inp = this.$refs.titleEditInput;
        if (inp) inp.focus();
      });
    },
    async commitEditTitle() {
      if (!this.conversationId || this.editingTitleId !== this.conversationId) return;
      const title = String(this.editingTitleValue || "").trim();
      if (title && title !== this.conversationTitle) {
        try {
          await userApi.updateConversation({ id: this.conversationId, title });
          this.conversationTitle = title;
        } catch (e) { /* 静默 */ }
      }
      this.editingTitleId = null;
      this.editingTitleValue = "";
    },
    cancelEditTitle() {
      this.editingTitleId = null;
      this.editingTitleValue = "";
    },

    // 模型加载
    async loadModels() {
      try {
        const list = await userApi.imageModels();
        this.imageModelsRaw = Array.isArray(list) ? list : [];
      } catch (e) {
        this.imageModelsRaw = [];
      }
      // 先确保有模型可选，再选第一个（覆盖默认空字符串），避免 pointsPerJob 计算时
      // currentImageModel 一直为 null。
      if (this.imageModelsRaw.length && !this.selectedModel) {
        this.selectedModel = this.imageModelsRaw[0].code;
      }
      // 默认分辨率要与模型配置兼容（若当前 selectedResolution 不在模型列表里则回退）
      const m = this.currentImageModel;
      if (m && Array.isArray(m.resolutions) && m.resolutions.length) {
        const allowed = m.resolutions.map(x => String(x).toUpperCase());
        if (allowed.indexOf(String(this.selectedResolution).toUpperCase()) < 0) {
          this.selectedResolution = String(m.resolutions[0]).toUpperCase();
        }
      }
    },

    // 任务列表
    async loadJobs(reset) {
      if (reset) {
        this.page = 1;
        this.hasMore = true;
        this.jobs = [];
        this.listLoading = true;
      } else if (!this.hasMore || this.loadingMore) return;
      else this.loadingMore = true;
      try {
        const res = await userApi.listQuickImageJobs({ page: this.page, pageSize: 20, conversationId: this.conversationId || undefined });
        const items = (res && res.items) || [];
        this.hasMore = !!(res && res.hasMore);
        const map = new Map(this.jobs.map(j => [j.taskId, j]));
        items.forEach(it => map.set(Number(it.taskId), { ...it }));
        this.jobs = [...map.values()].sort((a, b) => b.taskId - a.taskId);
        items.forEach(it => {
          if (it.status === "pending" || it.status === "running") this.startPoll(Number(it.taskId));
        });
        if (this.hasMore) this.page += 1;
      } finally {
        this.listLoading = false;
        this.loadingMore = false;
      }
    },
    onFeedScroll() {
      const el = this.$refs.feedScroll;
      if (el && el.scrollTop < 60) this.loadJobs(false);
    },
    scrollBottom() {
      const el = this.$refs.feedScroll;
      if (el) el.scrollTop = el.scrollHeight;
    },
    upsertJob(patch) {
      const id = Number(patch.taskId);
      if (!id) return;
      const i = this.jobs.findIndex(j => j.taskId === id);
      if (i < 0) this.jobs.unshift({ ...patch });
      else this.$set(this.jobs, i, { ...this.jobs[i], ...patch });
    },
    startPoll(taskId) {
      if (!taskId) return;
      this.ensureBatchPoll();
      if (!this._batchPollInFlight) void this.tickBatchPoll();
    },
    ensureBatchPoll() {
      if (this._batchPollTimer) return;
      const schedule = () => {
        if (this._batchPollTimer) { clearTimeout(this._batchPollTimer); this._batchPollTimer = null; }
        if (typeof document !== "undefined" && document.hidden) return;
        const active = (this.jobs || []).filter(j => j && (j.status === "pending" || j.status === "running")).length;
        if (active === 0) return;
        const base = Math.max(8000, this._batchPollMs || 10000);
        const jitter = Math.floor(base * 0.2 * (Math.random() * 2 - 1));
        const wait = Math.max(8000, base + jitter);
        this._batchPollTimer = setTimeout(async () => {
          this._batchPollTimer = null;
          if (this._batchPollInFlight) { schedule(); return; }
          this._batchPollInFlight = true;
          try { await this.tickBatchPoll(); }
          finally { this._batchPollInFlight = false; }
          schedule();
        }, wait);
      };
      schedule();
    },
    async tickBatchPoll() {
      const active = (this.jobs || []).filter(j => j && (j.status === "pending" || j.status === "running"));
      const taskIds = active.map(j => Number(j.taskId)).filter(n => n > 0);
      if (!taskIds.length) return;
      try {
        const resp = await userApi.batchGetQuickImageTaskStatus({ taskIds });
        const list = (resp && Array.isArray(resp.tasks)) ? resp.tasks : [];
        list.forEach(st => {
          this.upsertJob({
            taskId: Number(st.taskId),
            status: st.status,
            progress: st.progress,
            message: st.message,
            errorMessage: st.errorMessage,
            prompt: st.prompt,
            modelCode: st.modelCode || "",
            aspectRatio: st.aspectRatio || "",
            resolution: st.resolution || "",
            imageUrl: (st.result && st.result.imageUrl) || ""
          });
          if (st.status === "success" || st.status === "failed") {
            // 任务完成
          }
        });
        if (resp && resp.pollIntervalMs) {
          this._batchPollMs = Math.max(8000, Number(resp.pollIntervalMs) || 10000);
        }
      } catch (e) { /* 静默 */ }
    },

    // 输入处理
    onPromptInput() {
      // 纯文字输入，无特殊处理
    },
    getPromptText() {
      const ed = this.$refs.promptEditor;
      return ed ? String(ed.innerText || "").trim() : "";
    },

    // 提交
    async handleSubmit() {
      if (this.submitting) return;
      const prompt = this.getPromptText();
      if (!prompt) { window.alert("请先输入图片描述"); return; }
      if (!this.pointsPerJob) { window.alert("当前模型未配置扣费"); return; }
      this.submitting = true;
      try {
        // 首次生成（无 conversationId）时先创建对话，确保左侧历史列表立即同步出现新条目。
        // 与 CreatePage.submitImage 行为一致。
        let convId = this.conversationId;
        if (!convId) {
          const title = prompt.slice(0, 20) + (prompt.length > 20 ? "…" : "");
          try {
            const conv = await userApi.createConversation({ type: "quick_image", title });
            convId = Number(conv && conv.id) || null;
            if (convId) {
              this.conversationId = convId;
              this.conversationTitle = title;
              // URL 同步更新，便于刷新/分享；history.pushState 不会触发路由 watcher，需手动 replace
              if (this.$route && String(this.$route.query && this.$route.query.id || "") !== String(convId)) {
                const target = "/quick-image?id=" + convId;
                if (this.$router) this.$router.replace(target).catch(() => {});
              }
            }
          } catch (e) {
            // 创建对话失败也不阻塞任务提交，按旧逻辑继续（conversationId 仍为 null）
          }
        }
        const res = await userApi.createQuickImageTask({
          modelCode: this.selectedModel,
          prompt,
          aspectRatio: this.selectedAspectRatio,
          resolution: this.selectedResolution,
          conversationId: convId || undefined
        });
        const taskId = Number(res && res.taskId);
        if (!taskId) throw new Error("创建失败");
        this.upsertJob({
          taskId,
          status: "pending",
          progress: 0,
          message: "等待生成",
          prompt,
          modelCode: this.selectedModel,
          aspectRatio: this.selectedAspectRatio,
          resolution: this.selectedResolution
        });
        // 通知左侧历史对话列表刷新（即使 URL 未变，新创建的对话也应立即可见）
        try { this.$root && this.$root.$emit && this.$root.$emit("conversations-changed"); } catch (e) {}
        this.startPoll(taskId);
        this.$nextTick(() => this.scrollBottom());
        // 清空输入框
        const ed = this.$refs.promptEditor;
        if (ed) ed.innerText = "";
      } catch (e) {
        window.alert((e && e.message) || "提交失败");
      } finally {
        this.submitting = false;
      }
    },

    retryFromJob(job) {
      const ed = this.$refs.promptEditor;
      if (ed && job.prompt) ed.innerText = job.prompt;
      if (job.modelCode) this.selectedModel = job.modelCode;
      if (job.aspectRatio) this.selectedAspectRatio = job.aspectRatio;
      if (job.resolution) this.selectedResolution = job.resolution;
    },

    async regenerateFromJob(job) {
      const oldTaskId = Number(job && job.taskId) || 0;
      this.retryFromJob(job);
      try {
        await this.handleSubmit();
      } catch (e) {
        if (oldTaskId > 0) {
          const oldIdx = this.jobs.findIndex(j => Number(j.taskId) === oldTaskId);
          if (oldIdx >= 0) {
            this.$set(this.jobs, oldIdx, {
              ...this.jobs[oldIdx],
              status: "failed",
              errorMessage: String((e && e.message) || "提交失败")
            });
          }
        }
      }
    }
  }
};
</script>

<style scoped>
.quick-image-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 20px);
  background: #333333;
}
.quick-header {
  padding: 14px 18px 6px;
  flex-shrink: 0;
}
.quick-header-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.back-link {
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 13px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  flex-shrink: 0;
}
.back-link:hover { color: #19b06b; }
.conv-title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}
.conv-title {
  font-size: 18px;
  font-weight: 600;
  color: #e9e9e9;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
  user-select: none;
}
.conv-title-input {
  font-size: 18px;
  font-weight: 600;
  background: #2a2a2a;
  border: 1px solid #19b06b;
  border-radius: 6px;
  color: #e9e9e9;
  padding: 2px 8px;
  outline: none;
  flex: 1;
  min-width: 0;
}
.edit-title-btn {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 13px;
  padding: 2px 4px;
  border-radius: 4px;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s;
}
.conv-title-wrap:hover .edit-title-btn { opacity: 1; }
.edit-title-btn:hover { color: #19b06b; }
.quick-feed {
  flex: 1;
  overflow-y: auto;
  padding: 8px 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.feed-hint {
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
}
.feed-card {
  background: #1f1f1f;
  border-radius: 12px;
  padding: 12px 14px;
  margin: 0 auto;
  width: 100%;
}
.feed-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 6px;
}
.feed-time { flex-shrink: 0; color: #9ca3af; }
.feed-prompt {
  margin: 0;
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.6;
  color: #d1d5db;
  word-break: break-word;
}
.feed-status.ok {
  color: #19b06b;
  font-size: 13px;
  margin-top: 8px;
}
.feed-status.err {
  color: #a1a1a1;
  font-size: 13px;
  margin-top: 8px;
}
.link {
  border: none;
  background: none;
  color: #19b06b;
  cursor: pointer;
  margin-left: 6px;
}
.feed-image-box {
  margin-top: 8px;
}
.feed-image {
  width: 100%;
  max-height: 400px;
  object-fit: contain;
  border-radius: 8px;
  background: #000;
  cursor: zoom-in;
  display: block;
}
.feed-actions {
  margin-top: 8px;
  display: flex;
  gap: 8px;
}
.btn {
  border: 1px solid #19b06b;
  background: #19b06b;
  border-radius: 8px;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 13px;
  color: #fff;
}
.composer {
  flex-shrink: 0;
  background: #333333;
  border-top: 1px solid #000000;
  padding: 10px 14px 14px;
}
.prompt-wrap {
  margin-bottom: 10px;
}
.prompt-editor {
  background: #1f1f1f;
  border: 1px solid #3a3a3a;
  border-radius: 10px;
  min-height: 80px;
  padding: 12px;
  font-size: 14px;
  color: #e9e9e9;
  line-height: 1.6;
  outline: none;
  word-break: break-word;
  white-space: pre-wrap;
}
.prompt-editor:empty::before {
  content: attr(data-placeholder);
  color: #6b7280;
  pointer-events: none;
}
.prompt-editor:focus {
  border-color: #19b06b;
}
.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.tb-sel {
  height: 34px;
  background: #1f1f1f;
  border: 1px solid #3a3a3a;
  border-radius: 8px;
  color: #e9e9e9;
  font-size: 13px;
  padding: 0 8px;
  cursor: pointer;
}
.tb-send {
  height: 34px;
  padding: 0 16px;
  background: #19b06b;
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  margin-left: auto;
}
.tb-send:hover:not(:disabled) { background: #17a062; }
.tb-send:disabled { background: #2a5a3a; color: #6b7280; cursor: not-allowed; }
.mask {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.75);
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
}
.preview-wrap {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}
.preview-close {
  position: absolute;
  top: -16px;
  right: -16px;
  width: 32px;
  height: 32px;
  background: #2a2a2a;
  border: none;
  border-radius: 50%;
  color: #e9e9e9;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.preview-img {
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
  display: block;
}
</style>
